<template>
  <a-tree-select
      ref="treeSelectRef"
      :key="props.key"
      v-model:value="props.modelValue"
      style="width: 100%"
      :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
      allow-clear
      multiple
      tree-default-expand-all
      :tree-data="option ? option : props.treeData"
      :max-tag-placeholder="maxTagPlaceholder"
      tree-node-filter-prop="label"
      :open="dropdownOpen"
      @blur="dropdownOpen = false"
      @change="selectNodes"
      @click.stop="handleTagClick"
    >
      <template #tagRender="{ label, value, closable, onClose, option }">
        <a-tag
          v-if="props.multiple"
          :closable="closable"
          @close="onClose"
        >
          {{ option.fullName || label }}&nbsp;&nbsp;
        </a-tag>
        <template v-else>&nbsp;&nbsp;{{ option.fullName || label }}</template>
      </template>
      <slot></slot>
    </a-tree-select>
</template>

<script setup>
import { ref, defineProps, defineEmits, defineExpose, watchEffect, computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: [Array, String],
    default: () => []
  },
  url: {
    type: [Function], 
  },
  params: {
    type: [Object],
    default: () => ({})
  },
  onSetOptions: {
    type: [Function],
  },
  treeData: {
    type: [Array],
    default: () => []
  },
  show: {
    type: [Boolean],
    default: true,
  },
  key: {
    type: [String, Number],
    default: ''
  },
  multiple: {
    type: Boolean,
    default: false
  },
  treeSelectProps: {
    type: Object,
    default: () => ({})
  },
  showFullLabelPath: {
    type: Boolean,
    default: false
  }
});

const treeSelectRef = ref(null);
const dropdownOpen = ref(false);

const emits = defineEmits(['update:modelValue', 'change']);

// 初始化调用接口
const option = ref(false);
const init = (fun = props.url, params = props.params) => {
  fun(params).then(res => {
    if (props.onSetOptions) {
      option.value = props.onSetOptions(res);
    } else {
      option.value = res.data.records ? res.data.records : res.data;
    }
  })
};

watchEffect(() => {
  props.show && props.url && init(props.url, props.params);
});

const selectNodes = (value, label, extra) => {
  // 单选时，只取最后一个选择的选项
  if (!props.multiple && value?.length) {
    value = [value[value.length - 1]];
  }
  emits('update:modelValue', value);
  emits('change', value, label, extra);
}

// // 处理下拉框可见性变化
// const onDropdownVisibleChange = (open) => {
//   dropdownOpen.value = open;
// };

// 处理标签点击事件
const handleTagClick = (event) => {
  // 切换下拉框的打开状态
  dropdownOpen.value = !dropdownOpen.value;
  event.stopPropagation();
};

// 处理包装器点击事件
const handleWrapperClick = () => {
  // 点击包装器时也切换下拉框状态
  dropdownOpen.value = !dropdownOpen.value;
};

// 构建节点路径映射
const buildNodePathMap = (nodes, path = []) => {
  const map = new Map();
  
  if (!nodes || !Array.isArray(nodes)) return map;
  
  const labelField = props.treeSelectProps?.fieldNames?.label || 'label';
  const valueField = props.treeSelectProps?.fieldNames?.value || 'value';
  const childrenField = props.treeSelectProps?.fieldNames?.children || 'children';
  
  nodes.forEach(node => {
    const currentPath = [...path, { label: node[labelField], value: node[valueField] }];
    map.set(node[valueField], currentPath);
    
    if (node[childrenField]) {
      const childMap = buildNodePathMap(node[childrenField], currentPath);
      childMap.forEach((value, key) => map.set(key, value));
    }
  });
  
  return map;
};

const nodePathMap = computed(() => {
  return buildNodePathMap(option.value || props.treeData);
});

// 自定义标签显示函数
const maxTagPlaceholder = (omittedValues) => {
  return `+ ${omittedValues.length} ...`;
};

// 根据值获取完整路径标签
const getFullLabelPath = (value) => {
  if (!value) return '';

  const path = nodePathMap.value.get(value);
  if (path && path.length > 1) {
    return path.map(item => item.label).join('/');
  }
  return path && path.length ? path[0].label : '';
};

// 向父级暴露的属性和方法
defineExpose({
  init,
  getFullLabelPath,
  nodePathMap,
  treeSelectRef
});

</script>